import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  Tag,
  TagLabel,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';

/**
 * Confirmation prompt shown before any tag is taken off a record or deleted
 * from the tag list.
 *
 * Contact and deal tags are one shared set, so removing a tag reaches further
 * than the record the user is looking at. `description` and `note` are supplied
 * per call site so the wording always matches what will actually happen.
 */
export function ConfirmTagRemovalDialog({
  isOpen,
  cancelRef,
  title = 'Remove this tag?',
  tags = [],
  description,
  note,
  confirmLabel = 'Remove',
  confirmColorScheme = 'red',
  isProcessing = false,
  onCancel,
  onConfirm,
}) {
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onCancel}
      isCentered
    >
      <AlertDialogOverlay>
        {/* Above the Chakra modal layer, so this still reads on top when it is
            opened from inside the bulk-tag or merge modals. */}
        <AlertDialogContent zIndex="popover">
          <AlertDialogHeader fontSize="md" fontWeight="bold">
            {title}
          </AlertDialogHeader>

          <AlertDialogBody>
            <VStack align="stretch" spacing={3}>
              {tags.length > 0 && (
                <Wrap>
                  {tags.map((tagLabel) => (
                    <WrapItem key={tagLabel}>
                      <Tag size="sm" colorScheme="purple" borderRadius="full">
                        <TagLabel>{tagLabel}</TagLabel>
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              )}

              {description && <Text fontSize="sm">{description}</Text>}

              {note && (
                <Text fontSize="sm" color="gray.600">
                  {note}
                </Text>
              )}
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onCancel} isDisabled={isProcessing}>
              Cancel
            </Button>
            <Button
              colorScheme={confirmColorScheme}
              onClick={onConfirm}
              ml={3}
              isLoading={isProcessing}
            >
              {confirmLabel}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

/**
 * Promise-based access to the dialog above.
 *
 * Call sites stay a single guard — `if (!(await confirmTagRemoval({...}))) return;`
 * — instead of each component threading its own open/close state.
 *
 * Usage:
 *   const { confirmTagRemoval, dialog } = useTagRemovalConfirm();
 *   ...
 *   return (<>{...}{dialog}</>);
 *
 * @returns {{ confirmTagRemoval: (options: object) => Promise<boolean>, dialog: React.ReactNode }}
 */
export function useTagRemovalConfirm() {
  const [request, setRequest] = useState(null);
  const resolverRef = useRef(null);
  const cancelRef = useRef(null);

  const settle = useCallback((confirmed) => {
    setRequest(null);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    if (resolve) resolve(confirmed);
  }, []);

  const confirmTagRemoval = useCallback((options = {}) => (
    new Promise((resolve) => {
      // Anything still waiting is superseded, never left hanging.
      if (resolverRef.current) resolverRef.current(false);
      resolverRef.current = resolve;
      setRequest(options);
    })
  ), []);

  // A prompt left open when its host unmounts must resolve, or the awaiting
  // caller would hang forever.
  useEffect(() => () => {
    if (resolverRef.current) {
      const resolve = resolverRef.current;
      resolverRef.current = null;
      resolve(false);
    }
  }, []);

  const dialog = (
    <ConfirmTagRemovalDialog
      {...(request || {})}
      isOpen={Boolean(request)}
      cancelRef={cancelRef}
      onCancel={() => settle(false)}
      onConfirm={() => settle(true)}
    />
  );

  return { confirmTagRemoval, dialog };
}

export default ConfirmTagRemovalDialog;
